import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Trash2, Plus } from "lucide-react";
import AddBlockDropdown from "./add-block-dropdown";
import Block from "./block";
import type { BlockTree } from "@/blocks/block-tree";
import type {
  FeatureBlock,
  PointBlock,
  MultiPointBlock,
} from "@/blocks/geojson.blocks";
import { useRoom } from "@/hooks/use-room";

export default function Feature({
  block,
  tree,
}: {
  block: FeatureBlock;
  tree: BlockTree;
}) {
  const { docs } = useRoom();
  const [editingProperties, setEditingProperties] = useState(false);
  const [propertyKey, setPropertyKey] = useState("");
  const [propertyValue, setPropertyValue] = useState("");

  const properties = block.attributes.properties || {};
  const geometryId = block.content?.[0];
  const geometryBlock = geometryId
    ? tree.document.blocks?.find((b) => b.id === geometryId)
    : null;

  const handleAddProperty = () => {
    if (!docs || !propertyKey.trim()) return;

    const updatedProperties = {
      ...properties,
      [propertyKey.trim()]: propertyValue.trim() || null,
    };

    docs.updateBlockAttributes(block.id, {
      ...block.attributes,
      properties: updatedProperties,
    });

    setPropertyKey("");
    setPropertyValue("");
    setEditingProperties(false);
  };

  const handleRemoveProperty = (key: string) => {
    if (!docs) return;

    const updatedProperties = { ...properties };
    delete updatedProperties[key];

    docs.updateBlockAttributes(block.id, {
      ...block.attributes,
      properties: updatedProperties,
    });
  };

  const handleAddGeometry = (geometryType: "point" | "multipoint") => {
    if (!docs) return;

    const geometryAttributes =
      geometryType === "point"
        ? { coordinates: [5, 52] as [number, number] }
        : {
            coordinates: [
              [5, 52],
              [6, 53],
            ] as [number, number][],
          };

    docs.appendBlockToBlock(
      {
        ...block,
        deleted: block.deleted ?? false,
      },
      {
        type: geometryType,
        attributes: geometryAttributes,
        content: [],
        documentId: block.documentId,
        deleted: false,
      } satisfies Omit<PointBlock | MultiPointBlock, "id">,
    );
  };

  const handleRemoveGeometry = () => {
    if (!docs || !geometryId) return;

    // Remove from content
    docs.removeBlockFromBlock(
      {
        ...block,
        deleted: block.deleted ?? false,
      },
      geometryId,
    );
  };

  return (
    <div className="border rounded-lg p-4 space-y-3 bg-green-50/50">
      <div className="flex items-center justify-between">
        <span className="font-medium text-green-900">Feature</span>
      </div>

      {/* Properties Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-green-800">Properties</span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditingProperties(!editingProperties)}
            className="text-green-700 border-green-300 hover:bg-green-100"
          >
            <Plus className="h-3 w-3 mr-1" />
            Add Property
          </Button>
        </div>

        {Object.entries(properties).length > 0 ? (
          <div className="space-y-1">
            {Object.entries(properties).map(([key, value]) => (
              <div
                key={key}
                className="flex items-center justify-between bg-white p-2 rounded border"
              >
                <span className="text-sm">
                  <strong>{key}:</strong> {String(value)}
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveProperty(key)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  <Trash2 className="h-3 w-3" />
                </Button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-2 text-xs">
            No properties set
          </div>
        )}

        {editingProperties && (
          <div className="flex gap-2 items-center bg-white p-2 rounded border">
            <Input
              placeholder="Property name"
              value={propertyKey}
              onChange={(e) => setPropertyKey(e.target.value)}
              className="h-8"
            />
            <Input
              placeholder="Property value"
              value={propertyValue}
              onChange={(e) => setPropertyValue(e.target.value)}
              className="h-8"
            />
            <Button size="sm" onClick={handleAddProperty}>
              Add
            </Button>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setEditingProperties(false)}
            >
              Cancel
            </Button>
          </div>
        )}
      </div>

      {/* Geometry Section */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-green-800">Geometry</span>
          {!geometryBlock && (
            <AddBlockDropdown
              onAddBlock={(blockType) =>
                handleAddGeometry(blockType as "point" | "multipoint")
              }
              allowedTypes={["point", "multipoint"]}
              label="Add Geometry"
              size="sm"
              variant="outline"
            />
          )}
        </div>

        {geometryBlock ? (
          <div className="flex items-start gap-2 p-2 bg-white rounded border">
            <div className="flex-1">
              <Block block={geometryBlock} tree={tree} />
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRemoveGeometry}
              className="text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ) : (
          <div className="text-center text-muted-foreground py-2 text-xs">
            No geometry set
          </div>
        )}
      </div>
    </div>
  );
}
